import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "@phosphor-icons/react";
import { useLanguage, CONTACT } from "../i18n";

const Line = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden spotlight">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/4 bottom-0 w-[600px] h-[400px] rounded-full bg-brand-cyan/5 blur-[120px]" />
        <div className="absolute right-1/3 top-0 w-[400px] h-[300px] rounded-full bg-brand-green/5 blur-[100px]" />
      </div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          data-testid="hero-kicker"
          className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-brand-cyan mb-8"
        >
          {t.heroKicker}
        </motion.p>

        <h1 className="font-display font-bold tracking-tighter leading-[0.92] text-[13vw] sm:text-6xl md:text-7xl lg:text-8xl" data-testid="hero-heading">
          <Line delay={0.3}>{t.heroLine1}</Line>
          <Line delay={0.42}>{t.heroLine2}</Line>
          <Line delay={0.54} className="text-gradient-electric">{t.heroLine3}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          data-testid="hero-subheading"
          className="mt-10 max-w-xl font-mono text-sm md:text-base text-white/50 leading-relaxed"
        >
          {t.heroSub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-cyan text-ink px-7 py-3.5 font-mono text-xs uppercase tracking-widest font-bold hover:bg-brand-green transition-colors duration-300"
          >
            {t.heroCtaPrimary}
            <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white/70 hover:border-brand-cyan/50 hover:text-brand-cyan transition-colors duration-300"
          >
            {t.heroCtaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 flex flex-wrap gap-3"
          data-testid="hero-tags"
        >
          {[t.heroTag1, t.heroTag2, t.heroTag3, t.heroTag4].map((tag) => (
            <span key={tag} className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 font-mono text-[11px] text-white/50">
              {tag}
            </span>
          ))}
          <span className="rounded-full border border-brand-green/30 bg-brand-green/5 px-4 py-1.5 font-mono text-[11px] text-brand-green" data-testid="hero-availability-badge">
            ● {CONTACT.availability[lang]}
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
